/**
 * Evidence observation — `squadlab.evidence-observation.v1` (ADR 0067).
 *
 * The CANONICAL layer between a structured connector's raw vendor response and the
 * {@link EvidenceRecord} the engine scores. A structured source (World Bank, IMF, FRED, SEC EDGAR,
 * GaStat…) resolves a typed number the platform *already trusts*, so instead of re-fetching and
 * LLM-extracting it, the connector emits an `EvidenceObservation` carrying the number PLUS its full
 * lineage and data-quality metadata. That metadata **pre-seeds the seven qualification dimensions
 * deterministically** ({@link dimensionsFromObservation}) — an official measured figure earns a high,
 * defensible score with no probabilistic rating step.
 *
 * WHY here (`@squadlab/contracts`): the observation crosses engine → persistence → wire → replay →
 * the Data Explorer hub, so its vocabulary lives once (ADR 0017), versioned so old rows stay parseable.
 */

import { z } from 'zod';
import { QUALIFICATION_DIMENSIONS, type DimensionAssessment, type DimensionAssessments, type QualificationDimension, type Rating } from './evidence-filter.js';

/** Contract version tag for a persisted observation snapshot. */
export const EVIDENCE_OBSERVATION_SCHEMA_V1 = 'squadlab.evidence-observation.v1' as const;

// ── Data-quality vocabularies (higher-trust ↔ lower-trust, per axis) ──────────────────────────
/** How the value was obtained — measured/survey are strongest; proxy is a stand-in. */
export const OBSERVATION_METHODS = ['measured', 'survey', 'estimate', 'modeled', 'projected', 'proxy'] as const;
export type ObservationMethod = (typeof OBSERVATION_METHODS)[number];
/** Geographic granularity of the datum itself. */
export const GEOGRAPHY_LEVELS = ['national', 'subnational', 'regional', 'aggregate'] as const;
export type GeographyLevel = (typeof GEOGRAPHY_LEVELS)[number];
/** How the datum's geography relates to the idea's TARGET geography. */
export const GEOGRAPHY_MATCHES = ['exact', 'parent', 'proxy'] as const;
export type GeographyMatch = (typeof GEOGRAPHY_MATCHES)[number];
/** How much of the market the datum covers. */
export const COVERAGE_LEVELS = ['whole', 'partial', 'single'] as const;
export type CoverageLevel = (typeof COVERAGE_LEVELS)[number];
/** Whether the number is final or still moving. */
export const REVISION_STATES = ['final', 'provisional', 'estimate'] as const;
export type RevisionState = (typeof REVISION_STATES)[number];
/** The publisher's standing. */
export const SOURCE_TIERS = ['official-primary', 'official-secondary', 'commercial', 'community'] as const;
export type SourceTier = (typeof SOURCE_TIERS)[number];

/**
 * Boundary parser for one canonical observation from a structured connector — the value + its full
 * lineage + its data-quality metadata. Schema-first so {@link EvidenceObservation} can never drift
 * from what actually parses (parse, don't validate — ADR 0005). Field groups:
 *   Identity  — `metric` (canonical vocabulary, e.g. `population`), `geography` (ISO, e.g. `SAU`/`WLD`), `period`.
 *   Value     — `value`, `unit`, `unitBasis` (canonical basis when normalised).
 *   Provenance (the lineage chain) — vendor · endpoint · requestParams · fetchedAt · responseHash · parserId · rawFieldPath.
 *   Quality   — method · geographyLevel · geographyMatch (vs the idea's target) · coverage · revision · sourceTier · freshnessDays · vendorConfidence.
 */
export const EvidenceObservationSchema = z.object({
  metric: z.string().min(1).max(120),
  geography: z.string().min(1).max(32),
  period: z.string().min(1).max(32),
  value: z.number().finite(),
  unit: z.string().min(1).max(120),
  unitBasis: z.string().max(120).optional(),
  vendor: z.string().min(1).max(120),
  endpoint: z.string().min(1).max(2000),
  requestParams: z.record(z.string()).optional(),
  fetchedAt: z.string().min(1).max(64),
  responseHash: z.string().min(1).max(128),
  parserId: z.string().min(1).max(120),
  rawFieldPath: z.string().min(1).max(200),
  method: z.enum(OBSERVATION_METHODS),
  geographyLevel: z.enum(GEOGRAPHY_LEVELS),
  geographyMatch: z.enum(GEOGRAPHY_MATCHES),
  coverage: z.enum(COVERAGE_LEVELS),
  revision: z.enum(REVISION_STATES),
  sourceTier: z.enum(SOURCE_TIERS),
  freshnessDays: z.number().finite().nonnegative(),
  vendorConfidence: z.string().max(120).optional(),
});

/** A canonical, typed observation — the value + full lineage + data-quality metadata (ADR 0067 §2). */
export type EvidenceObservation = z.infer<typeof EvidenceObservationSchema>;

/** A persisted observation row: the observation + its storage identity (owner-scoped, linked to a source). */
export const EvidenceObservationRowSchema = EvidenceObservationSchema.extend({
  id: z.string().min(1).max(64),
  sourceId: z.string().min(1).max(64),
  ownerId: z.string().min(1).max(64),
  /** Epoch ms the row was written (injected clock — ADR 0002). */
  createdAt: z.number().finite(),
});
export type EvidenceObservationRow = z.infer<typeof EvidenceObservationRowSchema>;

// ── Deterministic quality → seven-dimension pre-seed (ADR 0067 §2) ────────────────────────────

const officialTier = (tier: SourceTier): boolean => tier === 'official-primary' || tier === 'official-secondary';

/** `sourceQuality`: an official primary dataset is the gold standard; community/scraped is weak. */
function rateSourceQuality(o: EvidenceObservation): Rating {
  if (o.sourceTier === 'official-primary') return 'high';
  if (o.sourceTier === 'official-secondary' || o.sourceTier === 'commercial') return 'medium';
  return 'low';
}

/** `segmentFit`: does the datum describe the RIGHT market? geography match + coverage together. */
function rateSegmentFit(o: EvidenceObservation): Rating {
  if (o.geographyMatch === 'exact' && o.coverage === 'whole') return 'high';
  if (o.geographyMatch === 'proxy' || o.coverage === 'single') return 'low';
  return 'medium';
}

/** `metricFit`: how directly the number stands for the asked quantity (method is the proxy signal). */
function rateMetricFit(o: EvidenceObservation): Rating {
  if (o.method === 'measured' || o.method === 'survey') return 'high';
  if (o.method === 'proxy') return 'low';
  return 'medium';
}

/** `freshness`: <1y is current; <3y usable for a fast market; older is stale. */
function rateFreshness(o: EvidenceObservation): Rating {
  if (o.freshnessDays <= 365) return 'high';
  if (o.freshnessDays <= 365 * 3) return 'medium';
  return 'low';
}

/** `coverage`: whole market vs a single provider/city. */
function rateCoverage(o: EvidenceObservation): Rating {
  if (o.coverage === 'whole') return 'high';
  if (o.coverage === 'single') return 'low';
  return 'medium';
}

/** `bias`: high = IMPARTIAL. An official statistical agency has no incentive to distort. */
function rateBias(o: EvidenceObservation): Rating {
  if (officialTier(o.sourceTier)) return 'high';
  if (o.sourceTier === 'commercial') return 'medium';
  return 'low';
}

const RATERS: Readonly<Record<QualificationDimension, (o: EvidenceObservation) => Rating>> = {
  sourceQuality: rateSourceQuality,
  segmentFit: rateSegmentFit,
  metricFit: rateMetricFit,
  freshness: rateFreshness,
  coverage: rateCoverage,
  bias: rateBias,
  // `lineage`: a structured observation carries a COMPLETE, traceable chain by construction
  // (vendor→endpoint→parser→field→value), so it is always maximally traceable — the whole point.
  lineage: () => 'high',
};

/** Human note per dimension explaining the deterministic rating (mirrors the LLM rater's `note`). */
function noteFor(dim: QualificationDimension, o: EvidenceObservation, rating: Rating): string {
  switch (dim) {
    case 'sourceQuality':
      return `${o.vendor} is a ${o.sourceTier.replace('-', ' ')} source → ${rating}.`;
    case 'segmentFit':
      return `Geography ${o.geographyMatch} (${o.geography}, ${o.geographyLevel}) with ${o.coverage} coverage → ${rating}.`;
    case 'metricFit':
      return `Value is ${o.method} for ${o.metric} → ${rating} metric fit.`;
    case 'freshness':
      return `Observed ${o.period}, ~${Math.round(o.freshnessDays)}d old → ${rating}.`;
    case 'coverage':
      return `${o.coverage} market coverage → ${rating}.`;
    case 'bias':
      return `${officialTier(o.sourceTier) ? 'Official agency — impartial' : 'Non-official publisher'} → ${rating}.`;
    case 'lineage':
      return `Full chain: ${o.vendor} · ${o.endpoint} · ${o.parserId} · ${o.rawFieldPath} → traceable.`;
  }
}

/**
 * Pre-seed the seven qualification dimensions DETERMINISTICALLY from a structured observation's
 * data-quality metadata (ADR 0067 §2) — the structured-source counterpart to the LLM source rater.
 * Same `{ rating, note }` shape, so a structured candidate slots into the pack exactly like a rated
 * web source, but with an auditable, reproducible score instead of a model judgment.
 */
export function dimensionsFromObservation(observation: EvidenceObservation): DimensionAssessments {
  return Object.fromEntries(
    QUALIFICATION_DIMENSIONS.map((dim): [QualificationDimension, DimensionAssessment] => {
      const rating = RATERS[dim](observation);
      return [dim, { rating, note: noteFor(dim, observation, rating) }];
    }),
  ) as DimensionAssessments;
}
